import { Button, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { FileTag } from "../../../../api/types/fileTypes";
import { getEntityTitle } from "../../../common/files/fileDetailsHelper";
import TagEditForm from "./TagEditForm";
import type { TagFormState, TagSettingsDialogState } from "./dialogState";

interface Props {
  dialogState: TagSettingsDialogState;
  onFormChange: (form: TagFormState) => void;
  onCancel: () => void;
  onSubmit: (newTag: FileTag) => void;
}

const createFileTag = withErrorHandling(adminApi.createFileTag);

const AddTagDialog = ({ dialogState, onFormChange, onCancel, onSubmit }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!dialogState.form.isValid || !dialogState.form.entityType) {
      return;
    }

    setSaving(true);
    const entityType = dialogState.form.entityType;
    const [resp, error] = await createFileTag({ name: dialogState.form.name.trim(), entityTypes: [entityType] });
    if (error) {
      logError(error, "createFileTag");
      sendNotificationError(error.message);
    } else {
      sendNotification(`Tag "${dialogState.form.name}" successfully added for entity ${getEntityTitle(entityType)}`);
      onSubmit(resp);
    }
    setSaving(false);
  };

  return (
    <SmallDialog open={dialogState.openDialog === "add"} onClose={onCancel}>
      <DialogTitle>Add Tag</DialogTitle>
      <DialogCloseButton onClick={onCancel} />

      <DialogContent sx={(t) => ({ px: t.spacing(4) })}>
        <TagEditForm form={dialogState.form} onChange={onFormChange} />
      </DialogContent>

      <DialogButtons onCancel={onCancel}>
        <Button
          disabled={!dialogState.form.isValid}
          variant="contained"
          color="primary"
          loading={saving}
          onClick={handleSubmit}
        >
          Add
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddTagDialog;
